import type { ReactNode } from "react";
import { Fragment, useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSmile,
  faPaperclip,
  faMicrophone,
  faTrashAlt,
  faPaperPlane,
  faUsers,
  faSignOutAlt,
  faBan,
} from "@fortawesome/free-solid-svg-icons";
import Button from "../layout/Button";
import Avatar from "../layout/Avatar";
import { WhatsappContact, WhatsappMessage } from "../../model/whatsapp.model";
import { WhatsappGroupsHook } from "../../hooks/useWhatsappGroups";
import {
  brPhoneVariants,
  formatMessageTime,
  formatPhoneDisplay,
  formatDayDivider,
  shouldShowDayDivider,
} from "../whatsapp/format";
import ManageParticipantsModal from "./ManageParticipantsModal";
import LeaveGroupConfirmModal from "./LeaveGroupConfirmModal";
import DeleteMessageConfirmModal from "../whatsapp/DeleteMessageConfirmModal";

// Mesmo tipo local de Whatsapp.page.tsx (não importado de lá — importar de
// volta criaria dependência circular, a página é quem importa este
// componente). Só usado pra resolver authorAgentId -> nome.
type Extension = { value: string; name: string };

// Duplicado de propósito de Whatsapp.page.tsx (importar de lá criaria
// dependência circular — a página importa este componente). Pequeno e sem
// lógica de negócio, risco de duplicar é baixo.
const QUICK_REACTIONS = ["👍", "❤️", "😂", "😮", "😢", "🙏"];
const EMOJIS = [
  "😀", "😁", "😂", "🙂", "😉", "😊", "😍", "🤔",
  "😅", "😢", "😭", "😡", "😱", "😴", "🤗", "🥳",
  "😎", "🤷", "🙋", "👍", "👎", "🙏", "👏", "💪",
  "🤝", "✋", "👌", "🤞", "👋", "❤️", "🔥", "🎉",
  "⭐", "💯", "✅", "❌", "⚠️", "ℹ️", "✔️", "📌",
  "📅", "⏰", "📎", "📷", "🎧", "📄", "💵", "📞",
];

// "text" chega do backend com "@{telefone}" literal (é o que a Z-API
// exige embutido na mensagem pra funcionar como menção de verdade) — cru
// demais pra exibir na nossa tela. Troca cada ocorrência pelo nome
// resolvido, destacado, sem alterar o texto original (é ele que volta se
// o atendente editar/responder a mesma mensagem).
function renderTextWithMentions(
  text: string,
  mentions: string[] | undefined,
  resolveName: (phone: string) => string,
  outgoing: boolean,
): ReactNode {
  if (!mentions || mentions.length === 0) return text;
  const pattern = new RegExp(`@(${mentions.join("|")})`, "g");
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    parts.push(
      <span
        key={key++}
        className={`font-semibold ${outgoing ? "text-white" : "text-purple"}`}
      >
        @{resolveName(match[1])}
      </span>,
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
}

function MessageTicks(props: { status?: WhatsappMessage["status"] }) {
  if (!props.status) return null;
  if (props.status === "failed") {
    return (
      <span
        className="inline-flex items-center gap-1 flex-none text-red"
        title="Não foi possível entregar esta mensagem"
      >
        <svg viewBox="0 0 16 16" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="1.4">
          <circle cx="8" cy="8" r="6.5" />
          <line x1="8" y1="5" x2="8" y2="8.5" strokeLinecap="round" />
          <circle cx="8" cy="11" r="0.6" fill="currentColor" stroke="none" />
        </svg>
        <span className="text-[10px] font-semibold">Não entregue</span>
      </span>
    );
  }
  const Check = ({ className }: { className?: string }) => (
    <svg
      viewBox="0 0 16 12"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M1 6.5L5 10.5L15 1" />
    </svg>
  );
  const color = props.status === "read" ? "text-[#53bdeb]" : "text-white/60";
  return (
    <span className={`inline-flex items-center flex-none ${color}`}>
      <Check className="w-3.5 h-3.5" />
      {props.status !== "sent" && <Check className="w-3.5 h-3.5 -ml-2.5" />}
    </span>
  );
}

// Header+mensagens+composer do grupo selecionado — sem Card em volta (fica
// dentro do mesmo Card de detalhe que o 1:1 usa, ver Whatsapp.page.tsx).
function GroupDetailPanel(props: {
  hook: WhatsappGroupsHook;
  contacts: WhatsappContact[];
  extensions: Extension[];
}) {
  const {
    selected,
    messages,
    composerText,
    sending,
    uploading,
    recording,
    recordSeconds,
    emojiOpen,
    setEmojiOpen,
    replyTo,
    setReplyTo,
    reactionPickerFor,
    setReactionPickerFor,
    deleteConfirmFor,
    setDeleteConfirmFor,
    deletingMessage,
    onDeleteMessage,
    composerRef,
    fileInputRef,
    messagesContainerRef,
    onMessagesScroll,
    insertEmoji,
    mentionQuery,
    onComposerChange,
    insertMention,
    closeMentionMenu,
    onSend,
    onReact,
    onAttachFile,
    startRecording,
    stopRecording,
    leaveConfirm,
    leavingGroup,
    requestLeaveGroup,
    cancelLeaveGroup,
    confirmLeaveGroup,
    showManageModal,
    setShowManageModal,
  } = props.hook;

  // Item destacado no pop-over de menção pra navegação por seta — vive
  // aqui (não no hook) porque mentionCandidates também só existe aqui (ver
  // abaixo, depende de props.contacts). Só reseta pro topo quando o menu
  // FECHA (mentionQuery volta a null) — continuar refinando o filtro
  // digitando mantém o índice destacado (clamp contra o tamanho da lista
  // filtrada logo abaixo cobre o caso dele sair da lista).
  const [mentionHighlight, setMentionHighlight] = useState(0);
  const highlightedItemRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (mentionQuery === null) setMentionHighlight(0);
  }, [mentionQuery]);
  useEffect(() => {
    highlightedItemRef.current?.scrollIntoView({ block: "nearest" });
  }, [mentionHighlight]);

  const contactByPhone = new Map(props.contacts.map((c) => [c.phone, c]));
  const extensionById = new Map(props.extensions.map((e) => [e.value, e]));

  // Nome pra exibir um participante por telefone — contato salvo primeiro,
  // depois o nome que a Z-API mandou no metadado do grupo, por último o
  // telefone formatado mesmo (participante sem nome nenhum resolvido).
  // Testa as duas variantes do 9º dígito porque o telefone do participante
  // vem do JID real do WhatsApp (ver backend), que pode divergir do
  // telefone digitado no contato salvo.
  const nameForPhone = (phone: string) => {
    const contact = brPhoneVariants(phone)
      .map((v) => contactByPhone.get(v))
      .find(Boolean);
    if (contact?.name) return contact.name;
    const participant = selected?.participants.find((p) => p.phone === phone);
    return participant?.name || formatPhoneDisplay(phone);
  };

  if (!selected) {
    return (
      <div className="flex-1 grid place-items-center text-gray-dark text-sm font-medium">
        Selecione um grupo na lista
      </div>
    );
  }

  // Participantes que batem com o que foi digitado depois do "@" — por
  // nome ou telefone, sem acento/caixa importar pro nome.
  const mentionCandidates =
    mentionQuery === null
      ? []
      : selected.participants
          .map((p) => ({ phone: p.phone, name: nameForPhone(p.phone) }))
          .filter((p) => {
            const q = mentionQuery.toLowerCase();
            if (!q) return true;
            return p.name.toLowerCase().includes(q) || p.phone.includes(q);
          });
  const clampedMentionHighlight =
    mentionCandidates.length > 0
      ? Math.min(mentionHighlight, mentionCandidates.length - 1)
      : 0;

  return (
    <>
      <div className="flex items-center justify-between gap-3 py-4 px-5 border-b border-white-dark flex-none">
        <div className="flex items-center gap-3 min-w-0">
          <Avatar avatar="" className="bg-gray-bluish w-10 h-10 flex-none" />
          <div>
            <div className="font-bold text-primary">{selected.name}</div>
            <button
              onClick={() => setShowManageModal(true)}
              className="text-xs text-gray-dark hover:text-purple flex items-center gap-1"
            >
              <FontAwesomeIcon icon={faUsers} className="text-[10px]" />
              {selected.participants.length}{" "}
              {selected.participants.length === 1 ? "participante" : "participantes"}
            </button>
          </div>
        </div>
        <Button
          outline
          color="gray"
          onClick={requestLeaveGroup}
          disabled={leavingGroup}
          isLoading={leavingGroup}
          className="!text-xs"
        >
          <FontAwesomeIcon icon={faSignOutAlt} className="mr-1.5" />
          Sair do grupo
        </Button>
      </div>

      <div
        ref={messagesContainerRef}
        onScroll={onMessagesScroll}
        className="flex-1 overflow-y-auto overflow-x-hidden p-5 flex flex-col gap-3 bg-white-mid"
      >
        {messages.map((m, i) => {
          // Separador de dia (pedido explícito do usuário, 2026-09-29) —
          // sem isso, mensagem de ontem e de hoje ficavam grudadas sem
          // nenhuma pista visual de que virou o dia.
          const dayDivider = shouldShowDayDivider(m.timestamp, messages[i - 1]?.timestamp) && (
            <div className="flex justify-center my-1">
              <span className="text-[11px] font-semibold text-gray-dark bg-white/80 rounded-full px-3 py-1 shadow-sm">
                {formatDayDivider(m.timestamp)}
              </span>
            </div>
          );

          // Mensagem de sistema (ex: "saiu do grupo") não é de um
          // participante nem do atendente — sem bolha, sem avatar, sem
          // ações, igual o próprio WhatsApp mostra esse tipo de evento.
          if (m.type === "system") {
            return (
              <Fragment key={m.id}>
                {dayDivider}
                <div className="flex justify-center">
                  <span className="text-[11px] text-gray-dark bg-white rounded-full px-3 py-1 shadow-sm text-center">
                    {nameForPhone(m.senderPhone || "")} {m.text}
                  </span>
                </div>
              </Fragment>
            );
          }

          const replied = m.replyToMessageId
            ? messages.find((mm) => mm.id === m.replyToMessageId)
            : null;
          const reactionChips = [m.reactions?.out, m.reactions?.in].filter(Boolean) as string[];
          const senderContact = m.senderPhone
            ? brPhoneVariants(m.senderPhone)
                .map((v) => contactByPhone.get(v))
                .find(Boolean)
            : null;
          // "in" = participante do grupo (fora do Voki) mandou; "out" = um
          // atendente nosso mandou pelo Voki — os dois casos precisam de
          // rótulo aqui, porque o grupo tem gente além de mim conversando
          // dos dois lados (diferente do 1:1, onde "out" só pode ser eu).
          const senderLabel =
            m.direction === "in"
              ? senderContact?.name || m.senderName || formatPhoneDisplay(m.senderPhone || "")
              : m.authorAgentId
                ? extensionById.get(m.authorAgentId)?.name || null
                : null;

          const actions = !m.deleted && (
            <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity pb-1 flex-none">
              <button
                onClick={() => setReactionPickerFor(m.id)}
                title="Reagir"
                className="w-6 h-6 rounded-full text-gray-dark hover:bg-white grid place-items-center text-xs"
              >
                🙂
              </button>
              <button
                onClick={() => setReplyTo(m)}
                title="Responder"
                className="w-6 h-6 rounded-full text-gray-dark hover:bg-white grid place-items-center text-xs"
              >
                ↩
              </button>
              {m.direction === "out" && (
                <button
                  onClick={() => setDeleteConfirmFor(m)}
                  title="Apagar para todos"
                  className="w-6 h-6 rounded-full text-gray-dark hover:bg-white grid place-items-center text-xs"
                >
                  <FontAwesomeIcon icon={faTrashAlt} />
                </button>
              )}
            </div>
          );

          return (
            <Fragment key={m.id}>
              {dayDivider}
              <div
                className={`group flex items-end gap-1 min-w-0 ${m.direction === "out" ? "justify-end" : ""}`}
              >
              {m.direction === "out" && actions}
              <div className="relative max-w-[62%] min-w-0">
                {reactionPickerFor === m.id && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setReactionPickerFor(null)}
                    ></div>
                    <div
                      className={`absolute -top-11 z-50 bg-white rounded-full drop-shadow-far px-2 py-1.5 flex gap-1.5 ${
                        m.direction === "out" ? "right-0" : "left-0"
                      }`}
                    >
                      {QUICK_REACTIONS.map((emoji) => (
                        <button
                          key={emoji}
                          onClick={() => onReact(m, emoji)}
                          className="text-base leading-none hover:scale-125 transition-transform"
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  </>
                )}

                <div
                  className={`text-xs drop-shadow break-words whitespace-pre-wrap ${
                    m.type === "image" || m.type === "video" ? "p-1.5" : "py-2.5 px-3.5"
                  } ${
                    m.direction === "out"
                      ? "bg-purple text-white rounded-[15px_4px_15px_15px]"
                      : "bg-white text-black rounded-[4px_15px_15px_15px]"
                  } ${reactionChips.length > 0 ? "mb-2.5" : ""}`}
                >
                  {m.deleted ? (
                    <span
                      className={`italic flex items-center gap-1 ${
                        m.direction === "out" ? "text-white/70" : "text-gray-dark"
                      }`}
                    >
                      <FontAwesomeIcon icon={faBan} className="text-[10px]" />
                      Mensagem apagada
                    </span>
                  ) : (
                    <>
                      {senderLabel && (
                        <div
                          className={`font-bold text-[11px] mb-0.5 ${
                            m.direction === "out" ? "text-white/90" : "text-purple"
                          }`}
                        >
                          {senderLabel}
                        </div>
                      )}
                      {replied && (
                        <div
                          className={`mb-1.5 border-l-2 rounded-5 px-2 py-1 text-[11px] truncate ${
                            m.direction === "out"
                              ? "border-white/60 bg-white/10 text-white/85"
                              : "border-purple/60 bg-purple/5 text-gray-dark"
                          }`}
                        >
                          {replied.deleted
                            ? "Mensagem apagada"
                            : replied.type === "text" || replied.type === "unsupported"
                              ? replied.text
                              : `${
                                  replied.type === "image"
                                    ? "📷 Imagem"
                                    : replied.type === "video"
                                      ? "🎥 Vídeo"
                                      : replied.type === "audio"
                                        ? "🎧 Áudio"
                                        : "📄 " + (replied.fileName || "Documento")
                                }`}
                        </div>
                      )}
                      {m.type === "image" && m.mediaUrl && (
                        <a href={m.mediaUrl} target="_blank" rel="noreferrer">
                          <img
                            src={m.mediaUrl}
                            alt={m.fileName || "imagem"}
                            className="rounded-10 max-w-full max-h-[260px] object-cover block"
                          />
                        </a>
                      )}
                      {m.type === "video" && m.mediaUrl && (
                        <video
                          controls
                          src={m.mediaUrl}
                          className="rounded-10 max-w-full max-h-[260px] block"
                        />
                      )}
                      {m.type === "document" && m.mediaUrl && (
                        <a
                          href={m.mediaUrl}
                          target="_blank"
                          rel="noreferrer"
                          className={`flex items-center gap-2 min-w-0 ${
                            m.direction === "out" ? "text-white" : "text-primary"
                          }`}
                        >
                          <span className="text-base flex-none">📄</span>
                          <span className="underline truncate">{m.fileName || "documento"}</span>
                        </a>
                      )}
                      {m.type === "audio" && m.mediaUrl && (
                        <audio controls src={m.mediaUrl} className="max-w-[220px]" />
                      )}
                      {/* Legenda de mídia (bug real: chegava do backend em
                          m.text, mas nunca era renderizada pra tipo != texto —
                          corrigido 2026-09-25) */}
                      {m.text &&
                        (m.type === "image" ||
                          m.type === "video" ||
                          m.type === "document" ||
                          m.type === "audio") && (
                          <div className="mt-1.5">
                            {renderTextWithMentions(
                              m.text,
                              m.mentions,
                              nameForPhone,
                              m.direction === "out",
                            )}
                          </div>
                        )}
                      {(m.type === "text" || m.type === "unsupported") &&
                        renderTextWithMentions(
                          m.text,
                          m.mentions,
                          nameForPhone,
                          m.direction === "out",
                        )}
                    </>
                  )}
                  <div className="flex items-center justify-end gap-1 mt-0.5">
                    <span
                      className={`text-[10px] ${
                        m.direction === "out" ? "text-white/70" : "text-gray-dark"
                      }`}
                    >
                      {formatMessageTime(m.timestamp)}
                    </span>
                    {m.direction === "out" && <MessageTicks status={m.status} />}
                  </div>
                </div>

                {reactionChips.length > 0 && (
                  <div
                    className={`absolute -bottom-2.5 bg-white rounded-full px-1.5 py-0.5 text-[11px] drop-shadow flex gap-0.5 ${
                      m.direction === "out" ? "right-2" : "left-2"
                    }`}
                  >
                    {reactionChips.join(" ")}
                  </div>
                )}
              </div>
              {m.direction === "in" && actions}
            </div>
            </Fragment>
          );
        })}
        {messages.length === 0 && (
          <div className="text-center text-xs text-gray-dark py-8">
            Nenhuma mensagem ainda neste grupo.
          </div>
        )}
      </div>

      {replyTo && (
        <div className="flex items-center justify-between gap-3 py-2 px-4 border-t border-white-dark bg-white-dark flex-none">
          <div className="min-w-0 border-l-2 border-purple pl-2.5">
            <div className="text-[10px] font-bold text-purple">
              Respondendo {replyTo.direction === "out" ? "sua mensagem" : "mensagem"}
            </div>
            <div className="text-[11px] text-gray-dark truncate max-w-[380px]">
              {replyTo.type === "text" || replyTo.type === "unsupported"
                ? replyTo.text
                : replyTo.type === "image"
                  ? "📷 Imagem"
                  : replyTo.type === "video"
                    ? "🎥 Vídeo"
                    : replyTo.type === "audio"
                      ? "🎧 Áudio"
                      : "📄 " + (replyTo.fileName || "Documento")}
            </div>
          </div>
          <button
            onClick={() => setReplyTo(null)}
            className="text-gray-dark hover:text-primary text-sm flex-none px-1"
            title="Cancelar resposta"
          >
            ✕
          </button>
        </div>
      )}

      {recording && (
        <div className="flex items-center gap-3 py-3.5 px-4 border-t border-white-dark flex-none">
          <button
            onClick={() => stopRecording(true)}
            title="Cancelar gravação"
            className="w-9 h-9 rounded-full text-gray-dark hover:bg-white-dark grid place-items-center flex-none text-sm"
          >
            <FontAwesomeIcon icon={faTrashAlt} />
          </button>
          <span className="w-2 h-2 rounded-full bg-red animate-pulse flex-none"></span>
          <span className="flex-1 text-xs font-bold text-primary tabular-nums">
            {Math.floor(recordSeconds / 60)}:{String(recordSeconds % 60).padStart(2, "0")}
          </span>
          <button
            onClick={() => stopRecording(false)}
            title="Enviar áudio"
            className="w-9 h-9 rounded-full bg-purple text-white grid place-items-center flex-none text-sm"
          >
            <FontAwesomeIcon icon={faPaperPlane} />
          </button>
        </div>
      )}

      {!recording && (
        <div className="flex items-end gap-2.5 py-3.5 px-4 border-t border-white-dark flex-none relative">
          <button
            onClick={() => setEmojiOpen((v) => !v)}
            className="w-9 h-9 rounded-full text-gray-dark hover:bg-white-dark grid place-items-center flex-none text-sm"
            title="Emoji"
          >
            <FontAwesomeIcon icon={faSmile} />
          </button>

          {emojiOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setEmojiOpen(false)}></div>
              <div className="absolute left-2 bottom-full mb-2 w-[280px] bg-white rounded-15 drop-shadow-far p-2.5 z-50 grid grid-cols-8 gap-1">
                {EMOJIS.map((emoji) => (
                  <button
                    key={emoji}
                    onClick={() => insertEmoji(emoji)}
                    className="text-lg leading-none py-1.5 rounded-10 hover:bg-white-dark grid place-items-center"
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </>
          )}

          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) onAttachFile(file);
              e.target.value = "";
            }}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="w-9 h-9 rounded-full text-gray-dark hover:bg-white-dark grid place-items-center flex-none text-sm disabled:opacity-50"
            title="Anexar arquivo"
          >
            {uploading ? "…" : <FontAwesomeIcon icon={faPaperclip} />}
          </button>

          <button
            onClick={startRecording}
            disabled={uploading}
            className="w-9 h-9 rounded-full text-gray-dark hover:bg-white-dark grid place-items-center flex-none text-sm disabled:opacity-50"
            title="Gravar áudio"
          >
            <FontAwesomeIcon icon={faMicrophone} />
          </button>

          <div className="relative flex-1 min-w-0">
            <textarea
              ref={composerRef}
              rows={1}
              className="w-full bg-white-dark rounded-[20px] py-2.5 px-4 text-xs outline-none resize-none leading-normal max-h-[120px] overflow-y-auto"
              placeholder="Escreva uma mensagem"
              value={composerText}
              onChange={(e) =>
                onComposerChange(e.target.value, e.target.selectionStart ?? e.target.value.length)
              }
              onKeyDown={(e) => {
                if (mentionQuery !== null) {
                  if (e.key === "ArrowDown") {
                    e.preventDefault();
                    if (mentionCandidates.length > 0)
                      setMentionHighlight((i) => (i + 1) % mentionCandidates.length);
                    return;
                  }
                  if (e.key === "ArrowUp") {
                    e.preventDefault();
                    if (mentionCandidates.length > 0)
                      setMentionHighlight(
                        (i) => (i - 1 + mentionCandidates.length) % mentionCandidates.length,
                      );
                    return;
                  }
                  if (e.key === "Enter" || e.key === "Tab") {
                    e.preventDefault();
                    if (mentionCandidates.length > 0) {
                      const chosen = mentionCandidates[clampedMentionHighlight];
                      insertMention(chosen.phone, chosen.name);
                    }
                    return;
                  }
                  if (e.key === "Escape") {
                    e.preventDefault();
                    closeMentionMenu();
                    return;
                  }
                }
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  onSend();
                }
              }}
              disabled={sending}
            />

            {mentionQuery !== null && (
              <>
                <div className="fixed inset-0 z-40" onClick={closeMentionMenu}></div>
                <div className="absolute left-0 right-0 bottom-full mb-2 max-h-[220px] overflow-y-auto bg-white rounded-15 drop-shadow-far p-1.5 z-50">
                  {mentionCandidates.length === 0 && (
                    <div className="text-xs text-gray-dark px-3 py-2">
                      Nenhum participante encontrado
                    </div>
                  )}
                  {mentionCandidates.map((p, i) => (
                    <button
                      key={p.phone}
                      ref={i === clampedMentionHighlight ? highlightedItemRef : undefined}
                      onClick={() => insertMention(p.phone, p.name)}
                      onMouseEnter={() => setMentionHighlight(i)}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-10 text-left ${
                        i === clampedMentionHighlight ? "bg-white-dark" : ""
                      }`}
                    >
                      <Avatar avatar="" className="bg-gray-bluish w-7 h-7 flex-none" />
                      <div className="min-w-0">
                        <div className="text-xs font-bold text-primary truncate">{p.name}</div>
                        <div className="text-[10px] text-gray-dark">
                          {formatPhoneDisplay(p.phone)}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
          <button
            onClick={onSend}
            disabled={sending || !composerText.trim()}
            className="w-9 h-9 rounded-full bg-purple text-white grid place-items-center flex-none text-sm disabled:opacity-50"
          >
            <FontAwesomeIcon icon={faPaperPlane} />
          </button>
        </div>
      )}

      {showManageModal && (
        <ManageParticipantsModal
          group={selected}
          contacts={props.contacts}
          extensions={props.extensions}
          onClose={() => setShowManageModal(false)}
        />
      )}

      {leaveConfirm && (
        <LeaveGroupConfirmModal
          groupName={leaveConfirm.groupName}
          isLastMember={leaveConfirm.isLastMember}
          onClose={cancelLeaveGroup}
          onConfirm={confirmLeaveGroup}
        />
      )}

      {deleteConfirmFor && (
        <DeleteMessageConfirmModal
          onClose={() => setDeleteConfirmFor(null)}
          onConfirm={onDeleteMessage}
          deleting={deletingMessage}
        />
      )}
    </>
  );
}

export default GroupDetailPanel;
